import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, FileText, MessageSquare, ClipboardList, Zap, Check, ChevronRight, ChevronDown, Download, Users } from "lucide-react";
import AuthModal from "@/components/AuthModal";
import UpgradeModal from "@/components/UpgradeModal";

const isIndia = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return tz.includes('Kolkata') || tz.includes('Calcutta') || tz.includes('India');
};

const HOW_STEPS = [
  { num: '01', title: 'Download LinkedIn PDF', desc: 'Profile → More → Save to PDF' },
  { num: '02', title: 'Paste job description', desc: 'From any job board — LinkedIn, Naukri, Indeed' },
  { num: '03', title: 'Generate with AI', desc: 'AI tailors everything in under 30 seconds' },
  { num: '04', title: 'Apply instantly', desc: 'Export PDF, copy cover letter, start interviewing' },
];

const TESTIMONIALS = [
  { img: 'https://images.unsplash.com/photo-1659355894117-0ae6f8f28d0b?w=80&h=80&fit=crop&crop=face', name: 'Arjun Sharma', role: 'SDE-2 at Flipkart', text: 'Got 3 interview calls in the first week. The ATS optimization is genuinely impressive.' },
  { img: 'https://images.unsplash.com/photo-1544264796-acfb69e05b37?w=80&h=80&fit=crop&crop=face', name: 'Priya Nair', role: 'Product Manager at Swiggy', text: 'Finally a tool that understands Indian job market. Cover letters are so natural, not generic.' },
  { img: 'https://images.unsplash.com/photo-1742981365880-698cfb84492d?w=80&h=80&fit=crop&crop=face', name: 'Rahul Mehta', role: 'Data Scientist at CRED', text: 'The interview prep questions were spot-on. I was asked 7 of the 10 questions in my actual interview.' },
];

const FAQS = [
  { q: 'Is ResumiQ free to use?', a: 'Yes, ResumiQ offers a free plan to build and download ATS-friendly resumes tailored for Indian job seekers.' },
  { q: 'Is ResumiQ ATS-friendly?', a: 'Yes, all resume templates on ResumiQ are optimized for Applicant Tracking Systems used by top Indian companies and MNCs.' },
  { q: 'Can freshers use ResumiQ?', a: 'Absolutely. ResumiQ has dedicated AI templates and suggestions for freshers, recent graduates, and entry-level candidates in India.' },
  { q: 'What makes ResumiQ different from other resume builders?', a: 'ResumiQ uses AI to generate tailored resume content based on the job role and industry, with a special focus on Indian job market requirements, company expectations, and ATS compatibility.' },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#2a2a38] rounded-sm overflow-hidden hover:border-[#c8a96e]/25 transition-colors duration-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <span className="text-[#f0ede8] text-sm font-medium pr-4">{question}</span>
        <ChevronDown size={16} className={`text-[#8a8799] flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 -mt-1">
          <p className="text-[#8a8799] text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function PricingCard({ plan, india }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const navigate = useNavigate();
  const handleCta = () => {
    if (plan.id === 'free') navigate('/generate');
    else setShowUpgrade(true);
  };
  return (
    <>
      <div
        data-testid={`pricing-card-${plan.id}`}
        className={`relative p-6 rounded-sm border transition-all duration-300 flex flex-col ${
          plan.featured ? 'border-[#c8a96e] bg-[#13131a]' : 'border-[#2a2a38] bg-[#13131a] hover:border-[#2a2a38]/60'
        }`}
        style={plan.featured ? { boxShadow: '0 0 40px -15px rgba(200,169,110,0.35)' } : {}}
      >
        {plan.featured && (
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#c8a96e] text-black text-xs px-3 py-0.5 rounded-full font-semibold">
            <Zap size={10} fill="currentColor" /> Most Popular
          </div>
        )}
        <div className="mb-5">
          <h3 className="text-[#f0ede8] font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.name}</h3>
          <div className="flex items-baseline gap-1 mt-2">
            <span className="text-3xl font-bold text-[#c8a96e]">{india ? plan.priceINR : plan.priceUSD}</span>
            <span className="text-[#8a8799] text-sm">{plan.period || '/mo'}</span>
          </div>
        </div>
        <ul className="space-y-2.5 flex-1 mb-6">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#f0ede8]">
              <Check size={12} className="text-[#4ade80] flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>
        <button
          onClick={handleCta}
          className={`w-full py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
            plan.featured ? 'bg-[#c8a96e] text-black hover:bg-[#e8c98e]' : 'border border-[#2a2a38] text-[#f0ede8] hover:border-[#c8a96e]/50'
          }`}
          style={plan.featured ? { boxShadow: '0 0 15px rgba(200,169,110,0.2)' } : {}}
        >
          {plan.cta}
        </button>
      </div>
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const india = isIndia();

  const PLANS = [
    {
      id: 'free', name: 'Free', priceINR: '₹0', priceUSD: '$0', period: '', featured: false, cta: 'Get started free',
      features: ['3 generations/month', 'Resume + Cover letter', 'ATS keyword analysis', 'PDF export']
    },
    {
      id: 'weekly', name: 'Weekly', priceINR: '₹350', priceUSD: '$4', period: '/week', featured: false, cta: 'Get Weekly plan',
      features: ['Unlimited generations', 'All 4 generation modes', '6 PDF templates', 'Full application history', 'ATS keyword analysis']
    },
    {
      id: 'pro', name: 'Pro', priceINR: '₹999', priceUSD: '$12', period: '/mo', featured: true, cta: 'Upgrade to Pro →',
      features: ['Unlimited generations', 'All 4 generation modes', '6 PDF templates', 'Full application history', 'Interview prep questions', 'LinkedIn bio + cold DMs']
    },
    {
      id: 'career', name: 'Career', priceINR: '₹1,999', priceUSD: '$24', period: '/mo', featured: false, cta: 'Get Career plan',
      features: ['Everything in Pro', 'Bulk generate (10 jobs)', 'Job application tracker', 'Recruiter cold email writer', 'Naukri profile optimizer']
    },
  ];

  return (
    <div className="bg-[#0a0a0f] text-[#f0ede8]">
      <Helmet>
        <title>ResumiQ – Free AI Resume Builder for Indian Job Seekers | ATS-Friendly</title>
        <meta name="description" content="Build a professional ATS-friendly resume in minutes with ResumiQ – India's AI-powered resume builder. Designed for freshers, IT professionals, and experienced candidates. Free to start." />
        <link rel="canonical" href="https://resumiq.in/" />
      </Helmet>

      {/* Top Resource Bar */}
      <div className="border-b border-[#2a2a38]/60 bg-[#0a0a0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-6 h-10 overflow-x-auto scrollbar-hide">
          <Link to="/blog" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">Blog</Link>
          <Link to="/resume-examples" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">Resume Gallery</Link>
          <Link to="/tools/ats-score-checker" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">Free ATS Checker</Link>
          <Link to="/ai-resume-builder" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">AI Resume Builder</Link>
          <Link to="/resume-builder-for-freshers" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">For Freshers</Link>
          <Link to="/ai-cover-letter-generator" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">Cover Letter</Link>
          <Link to="/interview-prep-ai" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors whitespace-nowrap">Interview Prep</Link>
        </div>
      </div>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left */}
          <div className="space-y-6 fade-up">
            <div className="inline-flex items-center gap-2 bg-[#c8a96e]/10 border border-[#c8a96e]/25 text-[#c8a96e] text-xs px-3 py-1.5 rounded-full">
              <Zap size={11} fill="currentColor" />
              Powered by AI
            </div>
            <h1 data-testid="hero-h1" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Build Your Dream Resume with AI –{' '}
              <span className="text-gold-gradient">Free for Indian Job Seekers</span>
            </h1>
            <p className="text-[#8a8799] text-base leading-relaxed max-w-md">
              Upload your LinkedIn PDF, paste a job description, and get a perfectly tailored resume, cover letter, and interview prep in under 30 seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-cta-btn"
                onClick={() => { setShowAuth(true); setAuthMode('signup'); }}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#c8a96e] text-black font-semibold rounded-sm hover:bg-[#e8c98e] hover:scale-[1.02] transition-all duration-200"
                style={{ boxShadow: '0 0 20px rgba(200,169,110,0.3)' }}
              >
                Get started free <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-login-btn"
                onClick={() => { setShowAuth(true); setAuthMode('login'); }}
                className="flex items-center justify-center gap-2 px-6 py-3 border border-[#2a2a38] text-[#f0ede8] rounded-sm hover:border-[#c8a96e]/50 transition-all duration-200"
              >
                Already have an account? Login
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[#2a2a38]">
              {[['30s', 'Generation time'], ['94%', 'ATS pass rate'], ['3x', 'More callbacks'], ['₹0', 'To start']].map(([val, label]) => (
                <div key={label} className="text-center sm:text-left">
                  <div className="text-xl font-bold text-[#c8a96e]">{val}</div>
                  <div className="text-[#8a8799] text-xs">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Feature cards */}
          <div className="space-y-3 fade-up-2">
            {[
              { icon: FileText, title: 'ATS-Optimized Resume', desc: 'Keyword-rich, beats 94% of automated screening systems' },
              { icon: MessageSquare, title: 'Compelling Cover Letter', desc: 'Personalized to the role, stands out from the crowd' },
              { icon: ClipboardList, title: 'Interview Prep Questions', desc: '10 likely questions with answer hints, ready in 30s' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div
                key={i}
                className="bg-[#13131a] border border-[#2a2a38] rounded-sm p-5 flex items-start gap-4 hover:border-[#c8a96e]/30 transition-all duration-300 group"
              >
                <div className="w-9 h-9 rounded-sm bg-[#c8a96e]/10 border border-[#c8a96e]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#c8a96e]/15 transition-colors duration-200">
                  <Icon size={16} className="text-[#c8a96e]" />
                </div>
                <div>
                  <h4 className="text-[#f0ede8] font-medium text-sm">{title}</h4>
                  <p className="text-[#8a8799] text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BAR */}
      <div className="border-y border-[#2a2a38] bg-[#13131a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          {['2,400+ resumes generated', '94% ATS pass rate', '30s average generation', 'Works with LinkedIn · Naukri · Indeed · Internshala'].map((item, i) => (
            <span key={i} className="flex items-center gap-2 text-[#8a8799] text-xs">
              <span className="w-1 h-1 rounded-full bg-[#c8a96e]" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* SEO SECTION 1: ATS-Friendly Resume Templates */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 data-testid="seo-h2-ats" className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              ATS-Friendly Resume Templates for Every Job Role
            </h2>
            <p className="text-[#8a8799] text-base leading-relaxed">
              ResumiQ's AI-powered resume builder creates resumes that pass Applicant Tracking Systems used by top Indian companies, MNCs, and startups. Whether you're applying for IT jobs, finance roles, or management positions, our templates are optimised for each industry.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {['IT & Software', 'Finance & Banking', 'Management', 'Marketing'].map((role, i) => (
              <div key={i} className="bg-[#13131a] border border-[#2a2a38] rounded-sm p-4 text-center hover:border-[#c8a96e]/30 transition-colors duration-200">
                <FileText size={20} className="text-[#c8a96e] mx-auto mb-2" />
                <p className="text-[#f0ede8] text-xs font-medium">{role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO SECTION 2: For Freshers and Experienced */}
      <section className="bg-[#13131a] border-y border-[#2a2a38] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 grid grid-cols-2 gap-3">
              {[
                { label: 'Freshers', sub: '0-1 years' },
                { label: 'Junior', sub: '1-3 years' },
                { label: 'Mid-level', sub: '3-7 years' },
                { label: 'Senior', sub: '10+ years' },
              ].map((exp, i) => (
                <div key={i} className="bg-[#0a0a0f] border border-[#2a2a38] rounded-sm p-4 text-center hover:border-[#c8a96e]/30 transition-colors duration-200">
                  <Users size={20} className="text-[#c8a96e] mx-auto mb-2" />
                  <p className="text-[#f0ede8] text-xs font-medium">{exp.label}</p>
                  <p className="text-[#8a8799] text-[0.65rem]">{exp.sub}</p>
                </div>
              ))}
            </div>
            <div className="order-1 md:order-2">
              <h2 data-testid="seo-h2-freshers" className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                AI Resume Builder for Freshers and Experienced Professionals
              </h2>
              <p className="text-[#8a8799] text-base leading-relaxed">
                From fresh graduates to senior professionals with 10+ years of experience, ResumiQ generates personalised resume content in minutes. Just enter your details and let our AI craft the perfect resume for your target job.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO SECTION 3: Download as PDF */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 data-testid="seo-h2-pdf" className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Download Your Professional Resume as PDF – Instantly
            </h2>
            <p className="text-[#8a8799] text-base leading-relaxed">
              Create, customise, and download your resume as a clean, professional PDF. No design skills needed. ResumiQ handles formatting, spacing, and layout so recruiters notice you.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="bg-[#13131a] border border-[#2a2a38] rounded-sm p-8 text-center max-w-xs w-full hover:border-[#c8a96e]/30 transition-colors duration-200">
              <Download size={40} className="text-[#c8a96e] mx-auto mb-4" />
              <p className="text-[#f0ede8] font-medium text-sm mb-1">6 Professional Templates</p>
              <p className="text-[#8a8799] text-xs">Classic, Modern, Minimal, Executive, Creative, ATS Clean</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#13131a] border-y border-[#2a2a38] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              How it works
            </h2>
            <p className="text-[#8a8799] text-sm mt-3">Four simple steps to your dream job</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {HOW_STEPS.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-[#0a0a0f] border border-[#2a2a38] rounded-sm p-5 hover:border-[#c8a96e]/30 transition-all duration-300 h-full">
                  <span className="text-[#c8a96e]/40 text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {step.num}
                  </span>
                  <h4 className="text-[#f0ede8] font-medium mt-2 mb-1">{step.title}</h4>
                  <p className="text-[#8a8799] text-xs leading-relaxed">{step.desc}</p>
                </div>
                {i < HOW_STEPS.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                    <ChevronRight size={14} className="text-[#2a2a38]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Loved by job seekers across India
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#13131a] border border-[#2a2a38] rounded-sm p-5 hover:border-[#c8a96e]/25 transition-all duration-300">
                <p className="text-[#f0ede8] text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.img} alt={`${t.name} testimonial`} loading="lazy" className="w-9 h-9 rounded-full object-cover border border-[#2a2a38]" />
                  <div>
                    <p className="text-[#f0ede8] text-xs font-medium">{t.name}</p>
                    <p className="text-[#8a8799] text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-[#13131a] border-y border-[#2a2a38] py-20 md:py-28" id="pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Simple, transparent pricing
            </h2>
            <p className="text-[#8a8799] text-sm mt-3">
              {india ? 'Prices in INR · UPI & cards accepted' : 'Prices in USD · All major cards accepted'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {PLANS.map(plan => (
              <PricingCard key={plan.id} plan={plan} india={india} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to land your dream job?
          </h2>
          <p className="text-[#8a8799] text-sm mb-6">Join 2,400+ professionals who upgraded their job search with AI</p>
          <button
            data-testid="footer-cta-btn"
            onClick={() => { setShowAuth(true); }}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#c8a96e] text-black font-semibold rounded-sm hover:bg-[#e8c98e] hover:scale-[1.02] transition-all duration-200"
            style={{ boxShadow: '0 0 20px rgba(200,169,110,0.3)' }}
          >
            Start for free <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#13131a] border-y border-[#2a2a38] py-20 md:py-28" id="faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-[#8a8799] text-sm mt-3">Everything you need to know about ResumiQ</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2a2a38] py-10 bg-[#0a0a0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded bg-[#c8a96e] flex items-center justify-center">
                  <FileText size={12} className="text-black" />
                </div>
                <span className="text-[#f0ede8] font-bold text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Resumiq<span className="text-[#c8a96e]">.in</span>
                </span>
              </div>
              <p className="text-[#8a8799] text-xs leading-relaxed max-w-xs">
                AI-powered resume and cover letter generator. Land more interviews with ATS-optimized resumes tailored to every job description.
              </p>
            </div>
            <div>
              <h4 className="text-[#f0ede8] text-xs font-semibold uppercase tracking-wider mb-3">Product</h4>
              <ul className="space-y-2">
                <li><a href="#pricing" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Pricing</a></li>
                <li><a href="#faq" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">FAQ</a></li>
                <li><Link to="/ai-resume-builder" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">AI Resume Builder</Link></li>
                <li><Link to="/ats-resume-builder" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">ATS Resume Builder</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[#f0ede8] text-xs font-semibold uppercase tracking-wider mb-3">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/resume-examples" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Resume Examples</Link></li>
                <li><Link to="/blog" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Blog & Tips</Link></li>
                <li><Link to="/resume-builder-for-freshers" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Resume for Freshers</Link></li>
                <li><Link to="/free-resume-builder-india" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Free Resume Builder</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[#f0ede8] text-xs font-semibold uppercase tracking-wider mb-3">Popular Roles</h4>
              <ul className="space-y-2">
                <li><Link to="/resume-examples/software-engineer" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Software Engineer</Link></li>
                <li><Link to="/resume-examples/data-analyst" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Data Analyst</Link></li>
                <li><Link to="/resume-examples/product-manager" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Product Manager</Link></li>
                <li><Link to="/resume-examples/chartered-accountant" className="text-[#8a8799] text-xs hover:text-[#c8a96e] transition-colors">Chartered Accountant</Link></li>
                <li><Link to="/resume-examples" className="text-[#c8a96e] text-xs hover:underline">View all 50+ roles →</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#2a2a38] pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#8a8799] text-xs">&copy; {new Date().getFullYear()} Resumiq.in &middot; AI-powered career tools</p>
            <p className="text-[#8a8799]/50 text-[0.6rem]">Made in India</p>
          </div>
        </div>
      </footer>

      {showAuth && <AuthModal mode={authMode} onClose={() => setShowAuth(false)} />}
    </div>
  );
}
